<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import StatusBadge from './StatusBadge.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { formatDateTime, formatNumber, isRunning } from '../helpers';

const props = defineProps({
  campaign: { type: Object, required: true },
});
const emit = defineEmits([
  'open',
  'edit',
  'duplicate',
  'delete',
  'pause',
  'resume',
]);
const { t, locale } = useI18n();

const c = computed(() => props.campaign);
const num = v => formatNumber(v, locale.value);
const dateLine = computed(() => {
  if (
    ['scheduled', 'draft'].includes(c.value.status) &&
    c.value.scheduled_at &&
    c.value.status === 'scheduled'
  ) {
    return t('WASAPI_CAMPAIGNS.LIST.SCHEDULED_FOR', {
      date: formatDateTime(c.value.scheduled_at, locale.value),
    });
  }
  return t('WASAPI_CAMPAIGNS.LIST.CREATED_AT', {
    date: formatDateTime(c.value.created_at, locale.value),
  });
});
const showStats = computed(
  () => !['draft', 'scheduled'].includes(c.value.status)
);
const percent = computed(() => c.value.progress?.percent || 0);
const canDelete = computed(() => !isRunning(c.value.status));
const canEdit = computed(() => ['draft', 'scheduled'].includes(c.value.status));
</script>

<template>
  <article
    class="flex flex-col gap-3 p-4 transition-colors rounded-xl outline outline-1 outline-n-weak bg-n-solid-1 hover:bg-n-alpha-1 cursor-pointer"
    @click="emit('open')"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col min-w-0 gap-1">
        <div class="flex items-center gap-2 min-w-0">
          <span
            class="size-4 shrink-0 text-n-slate-11"
            :class="
              c.channel === 'sms'
                ? 'i-lucide-message-square-text'
                : 'i-ri-whatsapp-line'
            "
          />
          <h3 class="text-base font-medium truncate text-n-slate-12">
            {{ c.title }}
          </h3>
          <StatusBadge :status="c.status" />
        </div>
        <p class="text-xs text-n-slate-11 truncate">
          {{ c.inbox?.name }} · {{ dateLine }}
          <template v-if="c.template_name">
            ·
            {{ t('WASAPI_CAMPAIGNS.LIST.TEMPLATE', { name: c.template_name }) }}
          </template>
        </p>
      </div>
      <div class="flex items-center gap-1 shrink-0" @click.stop>
        <Button
          v-if="c.status === 'processing'"
          icon="i-lucide-pause"
          variant="faded"
          color="amber"
          size="xs"
          :title="t('WASAPI_CAMPAIGNS.ACTIONS.PAUSE')"
          @click="emit('pause')"
        />
        <Button
          v-if="c.status === 'paused'"
          icon="i-lucide-play"
          variant="faded"
          color="teal"
          size="xs"
          :title="t('WASAPI_CAMPAIGNS.ACTIONS.RESUME')"
          @click="emit('resume')"
        />
        <Button
          v-if="canEdit"
          icon="i-lucide-pencil"
          variant="faded"
          color="slate"
          size="xs"
          :title="t('WASAPI_CAMPAIGNS.ACTIONS.EDIT')"
          @click="emit('edit')"
        />
        <Button
          icon="i-lucide-bar-chart-3"
          variant="faded"
          color="slate"
          size="xs"
          :title="t('WASAPI_CAMPAIGNS.ACTIONS.REPORT')"
          @click="emit('open', 'report')"
        />
        <Button
          icon="i-lucide-copy"
          variant="faded"
          color="slate"
          size="xs"
          :title="t('WASAPI_CAMPAIGNS.ACTIONS.DUPLICATE')"
          @click="emit('duplicate')"
        />
        <Button
          v-if="canDelete"
          icon="i-lucide-trash"
          variant="faded"
          color="ruby"
          size="xs"
          :title="t('WASAPI_CAMPAIGNS.ACTIONS.DELETE')"
          @click="emit('delete')"
        />
      </div>
    </div>
    <template v-if="showStats">
      <div
        v-if="isRunning(c.status)"
        class="w-full h-1.5 rounded-full bg-n-alpha-2 overflow-hidden"
      >
        <div
          class="h-full rounded-full"
          :class="c.status === 'paused' ? 'bg-n-amber-9' : 'bg-n-brand'"
          :style="{ width: `${percent}%` }"
        />
      </div>
      <div
        class="flex flex-wrap gap-x-5 gap-y-1 text-xs text-n-slate-11 tabular-nums"
      >
        <span
          ><strong class="text-n-slate-12">{{ num(c.audience) }}</strong>
          {{ t('WASAPI_CAMPAIGNS.LIST.AUDIENCE') }}</span
        >
        <span
          ><strong class="text-n-blue-11">{{ num(c.sent) }}</strong>
          {{ t('WASAPI_CAMPAIGNS.LIST.SENT') }}</span
        >
        <span
          ><strong class="text-n-teal-11">{{ num(c.delivered) }}</strong>
          {{ t('WASAPI_CAMPAIGNS.LIST.DELIVERED') }}</span
        >
        <span v-if="c.channel === 'whatsapp'"
          ><strong class="text-n-teal-11">{{ num(c.read) }}</strong>
          {{ t('WASAPI_CAMPAIGNS.LIST.READ') }}</span
        >
        <span
          ><strong class="text-n-ruby-11">{{ num(c.failed) }}</strong>
          {{ t('WASAPI_CAMPAIGNS.LIST.FAILED') }}</span
        >
      </div>
    </template>
  </article>
</template>
